import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import { canonicalMoney, sumMoney } from '../lib/money';
import { markPaidManually, cancelInvoice, markBadDebt } from '../lib/invoiceMutations';
import { lookupErrorCopy } from '../lib/errorMap';
import type { InvoiceRow, InvoiceSummary, AgingBuckets, InvoiceFilters } from '../types';
import { INVOICE_SEGMENTS, type InvoiceSegment } from '../types/invoiceSegments';
import type { SortDirection } from './useSortState';

type SegmentCounts = Record<InvoiceSegment, number>;
const ZERO_COUNTS: SegmentCounts = { active: 0, outstanding: 0, paid: 0, drafts: 0, cancelled: 0, all: 0 };

function formatDate(d: Date): string { return d.toISOString().split('T')[0]; }

function getFilterDateStart(range: InvoiceFilters['dateRange']): string | null {
  if (range === 'all') return null;
  const d = new Date();
  if (range === 'week') d.setDate(d.getDate() - 7);
  else if (range === 'month') d.setMonth(d.getMonth() - 1);
  else if (range === 'quarter') d.setMonth(d.getMonth() - 3);
  else d.setFullYear(d.getFullYear() - 1);
  return formatDate(d);
}

interface UseInvoicesOptions {
  from: number;
  to: number;
  sortColumn: string | null;
  sortDirection: SortDirection;
}

interface UseInvoicesResult {
  invoices: InvoiceRow[];
  totalCount: number | null;
  summary: InvoiceSummary;
  agingBuckets: AgingBuckets;
  segmentCounts: SegmentCounts;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  recordManualPayment: (invoice: InvoiceRow, opts: { method: string; amount: number; date: string; note: string }) => Promise<{ success: boolean; error?: string; alreadyPaid?: boolean }>;
  closeInvoiceWithoutPayment: (invoiceId: string, currentStatus: string) => Promise<{ success: boolean; newStatus?: string; error?: string }>;
  filters: InvoiceFilters;
  setFilters: (f: InvoiceFilters) => void;
  exportCSV: () => void;
}

const DEFAULT_OPTIONS: UseInvoicesOptions = { from: 0, to: 199, sortColumn: null, sortDirection: 'desc' };

export function useInvoices(businessId: string | undefined, options: UseInvoicesOptions = DEFAULT_OPTIONS): UseInvoicesResult {
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<InvoiceFilters>({ segment: 'active', dateRange: 'all', search: '' });

  // Summary + aging + segment counts are computed from separate queries (not paginated/segment-filtered)
  const [summary, setSummary] = useState<InvoiceSummary>({ totalOutstanding: 0, totalOverdue: 0, totalPaidThisMonth: 0, invoiceCount: 0, overdueCount: 0 });
  const [agingBuckets, setAgingBuckets] = useState<AgingBuckets>({
    current: { count: 0, amount: 0 }, days30: { count: 0, amount: 0 },
    days60: { count: 0, amount: 0 }, days90: { count: 0, amount: 0 },
    days90plus: { count: 0, amount: 0 },
  });
  const [segmentCounts, setSegmentCounts] = useState<SegmentCounts>(ZERO_COUNTS);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  const fetchAll = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      // Build paginated + filtered query
      let query = supabase
        .from('invoices')
        .select('id, invoice_number, client_name, client_email, client_phone, total, subtotal, tax_rate, tax_amount, status, due_date, paid_at, sent_at, payment_method, line_items, notes, created_at, share_token, stripe_payment_link_url', { count: 'exact' })
        .eq('business_id', businessId);

      // Segment-based status filtering (§2.6). written_off joins cancelled
      // as a terminal non-revenue state; both are excluded from Active.
      if (filters.segment === 'active') query = query.not('status', 'in', '("cancelled","draft","written_off")');
      else if (filters.segment === 'outstanding') query = query.in('status', ['sent', 'viewed', 'overdue']);
      else if (filters.segment === 'paid') query = query.eq('status', 'paid');
      else if (filters.segment === 'drafts') query = query.eq('status', 'draft');
      else if (filters.segment === 'cancelled') query = query.eq('status', 'cancelled');
      // 'all' = no filter
      const dateStart = getFilterDateStart(filters.dateRange);
      if (dateStart) query = query.gte('created_at', dateStart);
      if (debouncedSearch) {
        const q = `%${debouncedSearch}%`;
        query = query.or(`invoice_number.ilike.${q},client_name.ilike.${q},client_email.ilike.${q}`);
      }

      const sortCol = options.sortColumn ?? 'created_at';
      const sortAsc = options.sortColumn ? options.sortDirection === 'asc' : false;
      query = query.order(sortCol, { ascending: sortAsc }).range(options.from, options.to);

      // Summary queries (non-paginated)
      const now = new Date();
      const monthStart = formatDate(new Date(now.getFullYear(), now.getMonth(), 1));

      const [mainRes, outstandingRes, overdueRes, paidMonthRes, totalRes, agingRes, statusRes] = await Promise.all([
        query,
        supabase.from('invoices').select('total').eq('business_id', businessId).in('status', ['sent', 'viewed', 'overdue']),
        supabase.from('invoices').select('total').eq('business_id', businessId).eq('status', 'overdue'),
        supabase.from('invoices').select('total').eq('business_id', businessId).eq('status', 'paid').gte('paid_at', monthStart),
        supabase.from('invoices').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('invoices').select('total, due_date, status').eq('business_id', businessId).in('status', ['sent', 'viewed', 'overdue']),
        supabase.from('invoices').select('status').eq('business_id', businessId),
      ]);

      if (mainRes.error) throw mainRes.error;

      setInvoices((mainRes.data ?? []) as InvoiceRow[]);
      setTotalCount(mainRes.count ?? 0);

      // Compute summary — Rule #34 canonical money sums; Number() at consumer.
      const outstandingTotal = Number(sumMoney((outstandingRes.data ?? []).map((i: { total: number | null }) => canonicalMoney(i.total ?? 0))));
      const overdueTotal = Number(sumMoney((overdueRes.data ?? []).map((i: { total: number | null }) => canonicalMoney(i.total ?? 0))));
      const paidMonthTotal = Number(sumMoney((paidMonthRes.data ?? []).map((i: { total: number | null }) => canonicalMoney(i.total ?? 0))));
      setSummary({
        totalOutstanding: outstandingTotal,
        totalOverdue: overdueTotal,
        totalPaidThisMonth: paidMonthTotal,
        invoiceCount: totalRes.count ?? 0,
        overdueCount: (overdueRes.data ?? []).length,
      });

      // Compute aging buckets
      const buckets: AgingBuckets = {
        current: { count: 0, amount: 0 }, days30: { count: 0, amount: 0 },
        days60: { count: 0, amount: 0 }, days90: { count: 0, amount: 0 },
        days90plus: { count: 0, amount: 0 },
      };
      const today = Date.now();
      for (const inv of agingRes.data ?? []) {
        if (!inv.due_date) { buckets.current.count++; buckets.current.amount += inv.total; continue; }
        const daysOver = Math.floor((today - new Date(inv.due_date + 'T00:00:00').getTime()) / 86400000);
        if (daysOver <= 0) { buckets.current.count++; buckets.current.amount += inv.total; }
        else if (daysOver <= 30) { buckets.days30.count++; buckets.days30.amount += inv.total; }
        else if (daysOver <= 60) { buckets.days60.count++; buckets.days60.amount += inv.total; }
        else if (daysOver <= 90) { buckets.days90.count++; buckets.days90.amount += inv.total; }
        else { buckets.days90plus.count++; buckets.days90plus.amount += inv.total; }
      }
      setAgingBuckets(buckets);

      // Segment counts — bucket the unfiltered status list so tab labels
      // reflect the full set, not the currently-filtered rows.
      const counts: SegmentCounts = { ...ZERO_COUNTS };
      for (const row of statusRes.data ?? []) {
        for (const seg of INVOICE_SEGMENTS) {
          if (seg.match(row.status)) counts[seg.key]++;
        }
      }
      setSegmentCounts(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally { setLoading(false); }
  }, [businessId, options.from, options.to, options.sortColumn, options.sortDirection, filters.segment, filters.dateRange, debouncedSearch]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const rtTables = useMemo(() => ['invoices'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  const recordManualPayment = useCallback(async (
    invoice: InvoiceRow,
    opts: { method: string; amount: number; date: string; note: string }
  ): Promise<{ success: boolean; error?: string; alreadyPaid?: boolean }> => {
    if (!businessId) return { success: false, error: 'No business' };
    // Phase B.3.2: routed through canonical RPC via callMoneyRpc wrapper.
    // Decision #520 (M-A4 Session 112): userId arg removed; RPC body
    // resolves the actor server-side via get_public_user_id().
    const result = await markPaidManually(invoice.id, {
      amount: opts.amount,
      method: opts.method,
      receivedAt: new Date(opts.date + 'T12:00:00').toISOString(),
      notes: opts.note || null,
    });
    if (!result.success) {
      const msg = lookupErrorCopy('set_invoice_paid_manually', result.error.code);
      setError(msg);
      return { success: false, error: msg };
    }
    await fetchAll();
    return { success: true, alreadyPaid: result.alreadyExists };
  }, [businessId, fetchAll]);

  const closeInvoiceWithoutPayment = useCallback(async (
    invoiceId: string, currentStatus: string
  ): Promise<{ success: boolean; newStatus?: string; error?: string }> => {
    // System-inferred terminal state per D11: draft → cancelled (work never
    // happened); sent/viewed/overdue → written_off (work happened, no payment
    // collected). Tax-relevant distinction. Phase B.3.2: routed through the
    // canonical RPCs (cancel_invoice / mark_invoice_bad_debt) via callMoneyRpc.
    if (currentStatus === 'draft') {
      const result = await cancelInvoice(invoiceId, 'Closed without payment — draft cancelled');
      if (!result.success) {
        const msg = lookupErrorCopy('cancel_invoice', result.error.code);
        setError(msg);
        return { success: false, error: msg };
      }
      await fetchAll();
      return { success: true, newStatus: 'cancelled' };
    }
    const result = await markBadDebt(invoiceId, 'Closed without payment — written off');
    if (!result.success) {
      const msg = lookupErrorCopy('mark_invoice_bad_debt', result.error.code);
      setError(msg);
      return { success: false, error: msg };
    }
    await fetchAll();
    return { success: true, newStatus: 'written_off' };
  }, [fetchAll]);

  const exportCSV = useCallback(() => {
    const header = 'Invoice #,Client,Subtotal,Tax,Total,Status,Due Date,Issued,Paid Date,Payment Method';
    const rows = invoices.map(i =>
      `"${i.invoice_number}","${i.client_name ?? ''}",${i.subtotal},${i.tax_amount},${i.total},${i.status},${i.due_date ?? ''},${i.created_at.split('T')[0]},${i.paid_at?.split('T')[0] ?? ''},${i.payment_method ?? ''}`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `crewbit-invoices-${formatDate(new Date())}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [invoices]);

  return { invoices, totalCount, summary, agingBuckets, segmentCounts, loading, error, refresh: fetchAll, recordManualPayment, closeInvoiceWithoutPayment, filters, setFilters, exportCSV };
}
